import {
  Color3,
  MaterialPluginBase,
  Vector3,
  ShaderLanguage,
  Material,
  MaterialDefines,
  UniformBuffer,
  Nullable,
} from '@babylonjs/core'

const vertexDefinitions = `
  #ifdef GRID_PLUGIN_MATERIAL
    varying vec3 vPosition;
    varying vec3 vNormal;
  #endif
`

const vertexMainEnd = `
  #ifdef GRID_PLUGIN_MATERIAL
    vPosition = position;
    vNormal = normal;
  #endif
`
const fragmentDefinitions = `
  #ifdef GRID_PLUGIN_MATERIAL
    varying vec3 vPosition;
    varying vec3 vNormal;

    const float SQRT2 = 1.4142135624;
    float getDynamicVisibility(float position) {
      // Major grid line every Frequency defined in material.
      if (floor(position + 0.5) == floor(position / majorUnitFrequency + 0.5) * majorUnitFrequency)
      {
        return 1.0;
      }
      
      return minorUnitFrequency;
    }

    float getAnisotropicAttenuation(float differentialLength) {
      const float maxNumberOfLines = 10.0;

      return clamp(1.0 / (differentialLength + 1.0) - 1.0 / maxNumberOfLines, 0.0, 1.0);
    }
    
    float isPointOnLine(float position, float differentialLength) {
      float fractionPartOfPosition = position - floor(position + 0.5); // fract part around unit [-0.5, 0.5]
      fractionPartOfPosition /= differentialLength; // adapt to the screen space size it takes
      fractionPartOfPosition = clamp(fractionPartOfPosition, -1., 1.);
      
      float result = 0.5 + 0.5 * cos(fractionPartOfPosition * PI); // Convert to 0-1 for antialiasing.
      return result;
    }
    
    float contributionOnAxis(float position) {
      float differentialLength = length(vec2(dFdx(position), dFdy(position)));
      differentialLength *= SQRT2;  // Multiply by SQRT2 for diagonal length
      
      // Is the point on the line.
      float result = isPointOnLine(position, differentialLength);
      
      // Add dynamic visibility.
      float dynamicVisibility = getDynamicVisibility(position);
      result *= dynamicVisibility;
      
      // Anisotropic filtering.
      float anisotropicAttenuation = getAnisotropicAttenuation(differentialLength);
      result *= anisotropicAttenuation;
      
      return result;
    }
    
    float normalImpactOnAxis(float x) {
      float normalImpact = clamp(1.0 - 3.0 * abs(x * x * x), 0.0, 1.0);
      return normalImpact;
    }
  #endif GRID_PLUGIN_MATERIAL
`
const fragmentBeforeFog = `
  #ifdef GRID_PLUGIN_MATERIAL
    vec3 gridPos = vPosition / gridRatio;
    float x = contributionOnAxis(gridPos.x);
    float y = contributionOnAxis(gridPos.y);
    float z = contributionOnAxis(gridPos.z);
    
    vec3 normal = normalize(vNormal);
    x *= normalImpactOnAxis(normal.x);
    y *= normalImpactOnAxis(normal.y);
    z *= normalImpactOnAxis(normal.z);
    
    x *= axisVisibility.x;
    y *= axisVisibility.y;
    z *= axisVisibility.z;

    float grid = clamp(max(max(x, y), z), 0., 1.);

    color.rgb = mix(color.rgb, lineColor, grid);
  #endif
`

class GridPluginMaterial extends MaterialPluginBase {
  lineColor: Color3 = Color3.White()
  axisVisibility: Vector3 = Vector3.One()

  gridRatio: number = 1
  majorUnitFrequency: number = 10
  minorUnitFrequency: number = 0.33

  private _isEnabled: boolean = false

  get isEnabled() {
    return this._isEnabled
  }

  set isEnabled(enabled: boolean) {
    if (this._isEnabled === enabled) {
      return
    }
    this._isEnabled = enabled
    this.markAllDefinesAsDirty()
    this._enable(this._isEnabled)
  }

  isCompatible(shaderLanguage: ShaderLanguage) {
    switch (shaderLanguage) {
      case ShaderLanguage.GLSL:
        return true
      // TODO: Add support for WGSL
      case ShaderLanguage.WGSL:
      default:
        return false
    }
  }

  constructor(material: Material) {
    super(material, 'GridPluginMaterial', 200, { GRID_PLUGIN_MATERIAL: false })
  }

  prepareDefines(defines: MaterialDefines) {
    defines.GRID_PLUGIN_MATERIAL = this._isEnabled
  }

  getUniforms(shaderLanguage?: ShaderLanguage) {
    if (shaderLanguage === ShaderLanguage.WGSL) {
      // TODO: Add support for WGSL
      return {}
    }

    return {
      ubo: [
        { name: 'lineColor', size: 3, type: 'vec3' },
        { name: 'axisVisibility', size: 3, type: 'vec3' },

        { name: 'gridRatio', size: 1, type: 'float' },
        { name: 'majorUnitFrequency', size: 1, type: 'float' },
        { name: 'minorUnitFrequency', size: 1, type: 'float' },
      ],
      fragment: `
        #ifdef GRID_PLUGIN_MATERIAL
          uniform vec3 lineColor;
          uniform vec3 axisVisibility;

          uniform float gridRatio;
          uniform float majorUnitFrequency;
          uniform float minorUnitFrequency;
        #endif
      `,
    }
  }

  bindForSubMesh(uniformBuffer: UniformBuffer) {
    if (this._isEnabled) {
      uniformBuffer.updateColor3('lineColor', this.lineColor)
      uniformBuffer.updateVector3('axisVisibility', this.axisVisibility)

      uniformBuffer.updateFloat('gridRatio', this.gridRatio)
      uniformBuffer.updateFloat(
        'majorUnitFrequency',
        Math.round(this.majorUnitFrequency),
      )
      uniformBuffer.updateFloat('minorUnitFrequency', this.minorUnitFrequency)
    }
  }

  getClassName(): string {
    return 'GridPluginMaterial'
  }

  getCustomCode(
    shaderType: string,
    shaderLanguage?: ShaderLanguage,
  ): Nullable<{ [pointName: string]: string }> {
    // TODO: Add support for WGSL
    if (shaderLanguage === ShaderLanguage.WGSL) {
      return null
    }

    return shaderType === 'vertex'
      ? {
          CUSTOM_VERTEX_DEFINITIONS: vertexDefinitions,
          CUSTOM_VERTEX_MAIN_END: vertexMainEnd,
        }
      : {
          CUSTOM_FRAGMENT_DEFINITIONS: fragmentDefinitions,
          CUSTOM_FRAGMENT_BEFORE_FOG: fragmentBeforeFog,
        }
  }
}
export default GridPluginMaterial
